import { find } from 'lodash';
import * as React from 'react';
import { compose, withHandlers, withProps, withState } from 'recompose';

import Button from '@inqool/uas-web-core/es/components/controls//Button';

import { withNotifications } from '@components/hoc/withNotifications';
import { Loader, LoaderProgress } from '@components/loader';
import { ModalButton } from '@components/modalButton/ModalButton';
import {
  assignRecordDocument,
  cancelRecord,
  createAttachment,
  deleteAttachment,
  returnRecord
} from '@modules/records/_recordsActions';
import { addPragueTz } from '@utils/timezone';
import { EventsFormRecordServicesRecordCreate } from './EventsFormRecordServicesRecordCreate';
import { lockEvent } from './_eventsActions';

const enhance = compose<any, any>(
  withState('loader', 'setLoader', false),
  withState('loaderProgress', 'setLoaderProgress', 0),
  withNotifications({
    assignRecordDocument,
    deleteAttachment,
    createAttachment,
    returnRecord,
    lockEvent,
    cancelRecord
  }),
  withProps(({ eventId, checkedDocuments, documents, files, photos }) => {
    return {
      checkedDocumentsUpdated: documents.filter(d => checkedDocuments.some(cd => cd.id === d.id)),
      filesAndPhotos: [...files, ...photos],
      eventId: eventId
    };
  }),
  withHandlers({
    onSendRecord: ({ eventId, onRefresh, setLoader, returnRecordNotify, lockEventNotify, documents }) => async () => {
      const found = find(documents, d => d.record);

      if (found && found.record) {
        setLoader(true);

        const ok = await returnRecordNotify(found.record.id);
        await lockEventNotify(eventId);

        setLoader(false);

        if (ok) {
          onRefresh();
        }
      }
    },
    onAssignDocumentToRecord: ({ onRefresh, record, assignRecordDocumentNotify, setLoader }) => async document => {
      setLoader(true);
      if (document) {
        await assignRecordDocumentNotify({
          recordId: record.id,
          documentId: document.id
        });
      }
      setLoader(false);
      onRefresh();
    },
    onAssignDocumentsToRecord: ({
      onRefresh,
      checkedDocuments,
      record,
      assignRecordDocumentNotify,
      setLoader
    }) => async () => {
      setLoader(true);
      for (const document of checkedDocuments) {
        if (!document.record) {
          await assignRecordDocumentNotify({
            recordId: record.id,
            documentId: document.id
          });
        }
      }

      setLoader(false);
      onRefresh();
    },
    onSyncAttachments: ({
      documents,
      filesAndPhotos,
      deleteAttachmentNotify,
      createAttachmentNotify,
      onRefresh,
      setLoaderProgress
    }) => async () => {
      setLoaderProgress(1);
      const attachmentsTotal = documents.reduce((acc, d) => acc + d.attachments.length, 0);
      let count = 0;
      for (const document of documents) {
        for (const attachment of document.attachments) {
          count += 1;
          const file = filesAndPhotos.find(f => f.id === attachment.file.id);
          // if file is deleted, delete attachment too
          if (!file) {
            await deleteAttachmentNotify(document.id, attachment.id);
          }
          // if file was edited, update attachment with edited file
          const syncDate = new Date(attachment.syncDatetime);
          if (file && new Date(file.updated) > addPragueTz(syncDate)) {
            await createAttachmentNotify({ ...attachment, file, document: { id: document.id } }, document.id, true);
          }
          setLoaderProgress((count / (attachmentsTotal / 100)).toFixed(1));
        }
      }
      onRefresh();
      setLoaderProgress(0);
    },
    onCancelRecord: ({ onRefresh, setLoader, cancelRecordNotify, documents }) => async () => {
      const found = find(documents, d => d.record);

      if (found && found.record) {
        setLoader(true);

        const ok = await cancelRecordNotify(found.record.id);

        setLoader(false);

        if (ok) {
          onRefresh();
        }
      }
    }
  })
);

const View = ({
  setLoader,
  documents,
  eventId,
  setState,
  loader,
  onRefresh,
  onSyncAttachments,
  filesAndPhotos,
  loaderProgress,
  sps,
  onSendRecord,
  isDisabled,
  onCancelRecord,
  onAssignDocumentToRecord
}) => {
  const RecordCreate = React.useMemo(
    () => {
      return withProps({
        sps
      })(EventsFormRecordServicesRecordCreate);
    },
    [sps]
  );

  React.useEffect(
    () => {
      const documentWithRecord = documents.find(d => d.record !== undefined);
      const documentWithoutRecord = documents.find(d => d.record === undefined);
      if (documentWithRecord && documentWithoutRecord) {
        onAssignDocumentToRecord(documentWithoutRecord);
      }
    },
    [documents]
  );

  const documentOutsideRecordExists = documents.filter(d => d.record === undefined).length > 0;

  const recordExists = documents.filter(d => d.record !== undefined).length > 0;

  return (
    <React.Fragment>
      {!!loaderProgress && <LoaderProgress progress={loaderProgress} />}
      {loader && <Loader />}
      {!isDisabled && (
        <>
          <Button
            label="Odeslat spis"
            onClick={onSendRecord}
            disabled={documentOutsideRecordExists || !find(documents, d => d.record)}
          />

          <ModalButton
            label={'Nový spis'}
            style={{ margin: '0 10px' }}
            setLoader={setLoader}
            disabled={documents.length <= 0 || isDisabled || recordExists}
            documents={documents}
            eventId={eventId}
            setState={setState}
            ModalComponent={RecordCreate}
            onRefresh={onRefresh}
          />

          <Button
            label="Aktualizovat přílohy"
            onClick={onSyncAttachments}
            style={{ margin: '0 10px' }}
            disabled={
              documentOutsideRecordExists ||
              !documents.some(d =>
                d.attachments.some(a => {
                  const file = filesAndPhotos.find(f => f.id === a.file.id);
                  if (!file) {
                    return true;
                  }
                  const syncDate = addPragueTz(new Date(a.syncDatetime));
                  if (file && new Date(file.updated) > syncDate) {
                    return true;
                  }
                  return false;
                })
              ) ||
              isDisabled
            }
          />

          <Button
            label="Zrušit spis"
            onClick={onCancelRecord}
            disabled={documentOutsideRecordExists || !find(documents, d => d.record)}
          />
        </>
      )}
    </React.Fragment>
  );
};

export const ActionBar = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormRecordServicesActionbar.tsx